import styled, { useTheme } from "styled-components";
import { NewLookPageBody } from "features/portfolio/NewLook/styles.ts";
import React, { useCallback, useContext, useEffect, useState } from "react";
import WatchlistCards from "features/watchlist/WatchlistCards/WatchlistCards";
import Card from "common/Boxes/Card";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import AllMarketsTable from "features/watchlist/AllMarkets/AllMarketsTable";
import { CoinName, ExchangeName } from "@shared/types";
import RemoveFromWatchlistModal from "features/watchlist/common/RemoveFromWatchlistModal";
import { MarketToRemove } from "types";
import PageTabs from "common/Tabs/PageTabs";
import { useBottomNavigator, useDisablePageScroll } from "utils/hooks/misc";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context.tsx";
import BuyButton from "common/Buttons/BuyButton.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

const Watchlist = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.WATCHLIST);
  const { analytics } = useContext(AnalyticsContext);
  const { setShowSearch, setSearchOpen } = useContext(PageWrapperContext);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  useDisablePageScroll(isBottomNavigator);
  const { isNewLook } = useTheme();

  const [marketToRemove, setMarketToRemove] = useState<MarketToRemove>();
  const [tab, setTab] = useState<WatchlistTab>();
  const [shouldOpenSearch, setShouldOpenSearch] = useState(false);

  useEffect(() => {
    setShowSearch(tab === WatchlistTab.ALL_MARKETS);
  }, [tab]);

  useEffect(() => {
    if (tab === WatchlistTab.ALL_MARKETS) {
      setSearchOpen(shouldOpenSearch);
    } else {
      setShouldOpenSearch(false);
      setSearchOpen(false);
    }
  }, [tab, shouldOpenSearch]);

  useEffect(() => {
    analytics?.watchlistMarketsViewWebsite({});
  }, [analytics]);

  const showRemoveMarketModal = useCallback(
    (symbol: CoinName, exchange: ExchangeName) => {
      setMarketToRemove({ symbol, exchange });
    },
    []
  );

  const resetMarket = () => setMarketToRemove(undefined);

  const cards = <WatchlistCards onRemove={showRemoveMarketModal} />;
  const table = <AllMarketsTable />;

  const tabsItems = [
    {
      key: WatchlistTab.WATCHLIST,
      label: t(WatchlistTab.WATCHLIST),
      children: <MobileWrapper>{cards}</MobileWrapper>,
    },
    {
      key: WatchlistTab.ALL_MARKETS,
      label: t(WatchlistTab.ALL_MARKETS),
      children: table,
      forceRender: true,
    },
  ];

  const onAddClick = () => {
    setTab(WatchlistTab.ALL_MARKETS);
    setShouldOpenSearch(true);
  };

  return (
    <>
      {isBottomNavigator ? (
        <PageTabs
          items={tabsItems}
          defaultKey={tab}
          onTabChange={(tab) => setTab(tab.key as WatchlistTab)}
        />
      ) : isNewLook ? (
        <NewLookPageBody>
          <section>{cards}</section>
          <section>{table}</section>
        </NewLookPageBody>
      ) : (
        <Wrapper>
          {cards}
          <AllMarketsTableCard>{table}</AllMarketsTableCard>
        </Wrapper>
      )}
      <RemoveFromWatchlistModal
        marketToRemove={marketToRemove}
        resetMarket={resetMarket}
      />
      {tab === WatchlistTab.WATCHLIST && <BuyButton onClick={onAddClick} keepOnNewLookPhone={true} />}
    </>
  );
};

export default Watchlist;

enum WatchlistTab {
  WATCHLIST = "tabs.watchlist",
  ALL_MARKETS = "tabs.all_markets",
}

const Wrapper = styled.div`
  padding: 16px;
`;
const AllMarketsTableCard = styled(Card)`
  margin-top: 16px;
`;
const MobileWrapper = styled.div`
  height: 100%;
  overflow-y: auto;
  padding: ${(p) => (p.theme.isNewLook ? 16 : 12)}px;
`;
